import { PASSWORT_MINDESTLAENGE } from '@nac2s/shared'
import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { Hinweis } from '@/teile/Hinweis'
import { Logo } from '@/teile/Logo'
import { ApiError } from '../api/client.js'
import { loeseZugangEin } from '../api/session.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Passwort setzen über den Zugangslink (ADR-0004 Nachtrag 5).
 *
 * Die Administration stellt den Link in der Kundenverwaltung aus und gibt ihn
 * weiter — einen Mailversand gibt es noch nicht. Das Token steht im Fragment,
 * aus demselben Grund wie bei der Übergabe aus der App.
 *
 * Der Link wird erst beim **Absenden** entwertet, nicht beim Aufruf dieser
 * Seite. Wer die Seite versehentlich schließt, kann denselben Link noch
 * einmal öffnen; ein Mailfilter, der Links vorab besucht, verbrennt ihn nicht
 * (SECURITY.md §3).
 */

export function Zugang() {
  const [token, setToken] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const navigate = useNavigate()
  const { neuLaden } = useSitzung()

  useEffect(() => {
    const gelesen = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token')
    setToken(gelesen)
    // Aus der Adresszeile nehmen, damit es nicht im Verlauf stehen bleibt.
    // Im Zustand dieser Seite bleibt es, bis das Formular abgeschickt ist.
    if (gelesen) window.history.replaceState(null, '', window.location.pathname)
  }, [])

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    if (!token) return
    const formular = new FormData(ereignis.currentTarget)
    const passwort = String(formular.get('passwort') ?? '')
    const wiederholung = String(formular.get('wiederholung') ?? '')

    if (passwort !== wiederholung) {
      setMeldung('Die beiden Eingaben stimmen nicht überein.')
      return
    }
    if (passwort.length < PASSWORT_MINDESTLAENGE) {
      setMeldung(`Das Passwort braucht mindestens ${PASSWORT_MINDESTLAENGE} Zeichen.`)
      return
    }

    setLaeuft(true)
    setMeldung(null)
    try {
      await loeseZugangEin(token, passwort)
      await neuLaden()
      await navigate('/', { replace: true })
    } catch (fehler) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 0
          ? fehler.message
          : 'Der Link ist nicht mehr gültig. Bitte lassen Sie sich einen neuen ausstellen.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center p-8">
      <div className="relative flex flex-col gap-7">
        <Logo className="absolute bottom-full left-1/2 mb-8 w-[23.5rem] max-w-none -translate-x-1/2 [@media(min-height:880px)]:w-[30.5rem]" />

        {token === null ? (
          <Hinweis art="fehler" titel="Kein Zugangstoken in der Adresse">
            <p>
              Öffnen Sie den Link noch einmal, den Ihre Administration Ihnen gegeben hat. Er gilt 24
              Stunden und nur einmal.
            </p>
          </Hinweis>
        ) : (
          <>
            <div>
              <h1 className="font-display text-xl font-bold text-primary">Passwort festlegen</h1>
            </div>

            <form onSubmit={absenden} className="flex flex-col gap-4">
              <Input
                isRequired
                name="passwort"
                type="password"
                label={`Passwort (mindestens ${PASSWORT_MINDESTLAENGE} Zeichen)`}
                autoComplete="new-password"
                size="md"
              />
              <Input
                isRequired
                name="wiederholung"
                type="password"
                label="Passwort wiederholen"
                autoComplete="new-password"
                size="md"
              />

              {meldung ? (
                <p
                  role="status"
                  className="rounded-lg bg-error-primary p-3 text-sm text-error-primary"
                >
                  {meldung}
                </p>
              ) : null}

              <Button type="submit" size="lg" isLoading={laeuft}>
                Passwort setzen und anmelden
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  )
}
